import { createSlice } from "@reduxjs/toolkit";
import { buildOptions } from "../../../utils/gridstack";
import { getConsumeDataByPanelId } from "../../../modules/CustomDashboardModule/services/DashboardConfigurationService";

const initialState = {
  id: null,
  activeTabId: false,
  tabsIdsList: [],
  tabMap: {},
  loading: false,
  error: null,
};

const getPanelsFromOptions = (options) =>
  (options?.children ?? []).reduce((map, panel) => ({ ...map, panel }), {});

//slice for managing dashboard modules tabs and their content
export const homeDashboardSlice = createSlice({
  name: "homeDashboard",
  initialState,
  reducers: {
    setActiveTabId(state, action) {
      state.activeTabId = action.payload;
    },
    setId(state, action) {
      state.id = action.payload;
    },
    setTabs(state, action) {
      if (!Array.isArray(action.payload)) return;
      if (!action.payload.length) return;
      state.tabsIdsList = action.payload;
      state.activeTabId = action.payload.at(0).tabId;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    setTabContentById(state, action) {
      const { activeTabId } = state;
      if (action.payload?.length === 0) {
        state.tabMap[activeTabId] = {
          content: "welcome",
          props: {},
        };
        return;
      }

      const options = buildOptions(action.payload);
      const panels = getPanelsFromOptions(options);

      state.tabMap[activeTabId] = {
        content: "gridstack",
        props: { options, panels },
      };
    },
    addTab(state, action) {
      state.tabsIdsList.push(action.payload);
      state.activeTabId = action.payload.tabId;
    },
    updateTab(state, action) {
      const { tabId, updatedTab } = action.payload;
      const tabIndex = state.tabsIdsList.findIndex(
        (tab) => tab.tabId === tabId
      );
      if (tabIndex !== -1) {
        state.tabsIdsList[tabIndex] = {
          ...state.tabsIdsList[tabIndex],
          ...updatedTab,
        };
      }
    },
    deleteTab(state, action) {
      const deletedId = action.payload;
      const { activeTabId } = state;

      const oldList = state.tabsIdsList;
      const newList = oldList.filter((tab) => tab.tabId !== deletedId);

      state.tabsIdsList = newList;
      delete state.tabMap[deletedId];

      if (newList.length > 0) {
        const removedIndex = oldList.findIndex((t) => t.tabId === deletedId);
        const nextTab = newList[removedIndex] || newList[newList.length - 1];
        if (activeTabId === deletedId) state.activeTabId = nextTab.tabId;
      } else {
        state.activeTabId = null;
      }
    },
    /**
     * action used to upgrade welcome -> grid-stack
     */
    upgradePanelContentToGridStack(state, action) {
      const { activeTabId } = state;
      const tab = JSON.parse(JSON.stringify(state.tabMap[activeTabId]));

      if (!tab) return;
      if (tab?.content !== "welcome") return;

      // payload is one single panel
      const options = buildOptions(action.payload);
      const panels = getPanelsFromOptions(options);

      state.tabMap[activeTabId] = {
        content: "gridstack",
        props: { options, panels },
      };
    },
    resetTabContent(state, _) {
      const { activeTabId } = state;
      state.tabMap[activeTabId] = {
        content: "welcome",
        props: {},
      };
    },
    updatePanelData(state, action) {
      const { activeTabId } = state;
      const { panelId, newData } = action.payload;

      if (
        !state.tabMap[activeTabId] ||
        state.tabMap[activeTabId].content !== "gridstack"
      ) {
        return;
      }

      const gridStackProps = state.tabMap[activeTabId].props;
      const options = gridStackProps.options;

      if (options && options.children) {
        const updatedChildren = options.children.map((panel) => {
          if (panel.id === panelId) {
            // Actualizar el contenido del panel con los nuevos datos
            try {
              const currentContent = JSON.parse(panel.content);
              const updatedContent = {
                ...currentContent,
                props: {
                  ...currentContent.props,
                  data: newData.data || currentContent.props.data,
                  metadata: newData.metadata || currentContent.props.metadata,
                },
              };

              return {
                ...panel,
                content: JSON.stringify(updatedContent),
              };
            } catch (error) {
              console.error("Error updating panel content:", error);
              return panel;
            }
          }
          return panel;
        });

        state.tabMap[activeTabId].props.options.children = updatedChildren;
      }
    },

    updatePanelContentInTab(state, action) {
      const { activeTabId } = state;
      const { panelId, newData } = action.payload;

      const tabContent = state.tabMap[activeTabId];
      if (!tabContent || tabContent.content !== "gridstack") {
        console.warn(`Tab ${activeTabId} is not gridstack or doesn't exist`);
        return;
      }

      const panels = tabContent.props?.panels;

      // manage panel lookup
      let targetPanel = null;
      let targetPanelKey = null;

      // search panel id in different structures
      if (panels) {
        // Structure 1: panels.panel
        if (panels.panel && panels.panel.id === panelId) {
          targetPanel = panels.panel;
          targetPanelKey = "panel";
        }
        // Structure 2: panels[panelId] (direct structure)
        else if (panels[panelId]) {
          targetPanel = panels[panelId];
          targetPanelKey = panelId;
        }
        // Structure 3: search all properties of panels
        else {
          Object.keys(panels).forEach((key) => {
            const panel = panels[key];
            if (panel && (panel.id === panelId || parseInt(key) === panelId)) {
              targetPanel = panel;
              targetPanelKey = key;
            }
          });
        }
      }

      if (targetPanel && targetPanelKey) {
        try {
          const panelContent = JSON.parse(targetPanel.content);
          //update only the data property
          panelContent.props.data = newData;
          // transform back to JSON string
          panels[targetPanelKey].content = JSON.stringify(panelContent);
        } catch (error) {
          console.error(`Error updating panel ${panelId} content:`, error);
        }
      } else {
        console.warn(`Panel ${panelId} not found in structure`, {
          availablePanels: panels ? Object.keys(panels) : "No panels",
        });
      }
    },
  },
});

export const actions = homeDashboardSlice.actions;

const selectHomeDashboardId = (state) => state.homeDashboard.id;
const selectHomeDashboardActiveTabId = (state) =>
  state.homeDashboard.activeTabId;
const selectHomeDashboardTabs = (state) =>
  state.homeDashboard.tabsIdsList ?? [];
const selectHomeDashboardTabContent = (state) => {
  const { activeTabId } = state.homeDashboard;
  if (typeof activeTabId !== "number") return;
  if (!(activeTabId in state.homeDashboard.tabMap)) return null;
  return state.homeDashboard.tabMap[activeTabId];
};

const selectIsHomeDashboardLoading = (state) => state.homeDashboard.loading;

export const selectors = {
  selectHomeDashboardId,
  selectHomeDashboardActiveTabId,
  selectHomeDashboardTabs,
  selectHomeDashboardTabContent,
  selectIsHomeDashboardLoading,
};

export const homeDashboardReducer = homeDashboardSlice.reducer;

const updatePanelDataWithSorting =
  ({ panelId, orderBy }) =>
  async (dispatch, _) => {
    try {
      const body = {
        search: "",
        page: 0,
        pageSize: 10,
        orderBy: orderBy,
        filters: [],
      };

      const apiResponse = await getConsumeDataByPanelId(panelId, body);

      const transformedData = apiResponse.data.map((row) => {
        const obj = {};
        apiResponse.columns.forEach((column, index) => {
          obj[column] = row[index];
        });
        return obj;
      });

      dispatch(
        actions.updatePanelContentInTab({
          panelId,
          newData: transformedData,
        })
      );
    } catch (error) {
      console.error(`Error updating panel ${panelId} with sorting:`, error);
      dispatch(actions.setError(error.message || "Error updating panel data"));
    }
  };

export const queries = {
  updatePanelDataWithSorting,
};
